import { setNotification } from "features/Notification/notificationSlice";
import React, { useCallback, useEffect, useState } from "react";
import Modal from "react-bootstrap/Modal";
import { useDispatch } from "react-redux";
import authHeader from "services/auth-header";
import API from "services/axios";
import {
  INFORMATION_PDFS,
  fileNameOf,
  openStoredPdf,
  toHref,
} from "./informationFiles";

/**
 * Product Information -- lays out both halves of the tab (user, 2026-10-16):
 *   - left: the Default card (`defaultCard`, from SProductInformation). It
 *     stays put; Add / Edit open the location's form in a MODAL on top (user,
 *     2026-10-16, reverting the in-place version).
 *   - right: this product's values for the locations that need their own.
 *     Only locations that have a row are listed; Add picks a location not used
 *     yet, then the three values.
 *
 * Rows live in s_productLocationInfos (nova-api). Endpoints:
 *   GET   /sales/product/:id/location-information        list
 *   PATCH /sales/product/location-information            url (creates the row)
 *   PATCH /sales/product/information-file  + locationId  upload a PDF
 *   POST  /sales/product/information-file/delete + locationId  remove a PDF
 *   POST  /sales/product/location-information/delete     remove the row + PDFs
 *
 * PDFs are picked in the form and uploaded when Save / Add is pressed; a
 * saved PDF marked for removal is deleted then too, so Cancel undoes both.
 */

const EMPTY_FILES = { productImagePdf: null, productSizeChartPdf: null };

export default function ProductLocationOverrides({
  formData,
  isUpdateMode,
  defaultCard,
}) {
  const dispatch = useDispatch();
  const [locations, setLocations] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  // null = the modal is closed; { locationId, isNew } = it is open on that one
  const [editing, setEditing] = useState(null);
  const [url, setUrl] = useState("");
  const [picked, setPicked] = useState(EMPTY_FILES); // File per field
  const [removing, setRemoving] = useState({}); // field -> true
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const notify = (message, type) =>
    dispatch(setNotification({ message, type }));
  const errorText = (err) => err?.response?.data?.message || err.message;
  const productId = formData?.id;

  useEffect(() => {
    API.get("/locations", { headers: authHeader() })
      .then(({ data }) =>
        setLocations(
          (Array.isArray(data) ? data : []).sort(
            (a, b) => (a.sortId ?? 0) - (b.sortId ?? 0) || a.id - b.id,
          ),
        ),
      )
      .catch((err) => notify(errorText(err), "error"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadRows = useCallback(async () => {
    if (!isUpdateMode || !productId) return;
    try {
      setLoading(true);
      const { data } = await API.get(
        `/sales/product/${productId}/location-information`,
        {
          headers: authHeader(),
        },
      );
      setRows(data?.data || []);
    } catch (err) {
      notify(errorText(err), "error");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUpdateMode, productId]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  const locationName = (id) =>
    locations.find((l) => l.id === id)?.locationName || `Brand ${id}`;
  const usedIds = new Set(rows.map((r) => r.locationId));
  const available = locations.filter((l) => !usedIds.has(l.id));
  const current = editing
    ? rows.find((r) => r.locationId === editing.locationId) || {}
    : {};

  const openForm = (row) => {
    setError("");
    setPicked(EMPTY_FILES);
    setRemoving({});
    setUrl(row?.productUrl || "");
    setEditing(
      row
        ? { locationId: row.locationId, isNew: false }
        : { locationId: "", isNew: true },
    );
  };
  const closeForm = () => setEditing(null);

  const pickFile = (field, file) => {
    setError("");
    if (!file) return;
    if (file.type && file.type !== "application/pdf") {
      setError("That is not a PDF.");
      return;
    }
    setPicked((prev) => ({ ...prev, [field]: file }));
    setRemoving((prev) => ({ ...prev, [field]: false }));
  };

  const save = async () => {
    setError("");
    const locationId = Number(editing.locationId);
    if (!locationId) return setError("Choose a brand");
    const anything =
      url.trim() ||
      INFORMATION_PDFS.some(({ field }) => picked[field] || current[field]);
    if (editing.isNew && !anything)
      return setError("Add a Product Url or a PDF for this brand");

    try {
      setSaving(true);
      // The Url first: it creates the row the PDFs then hang on.
      await API.patch(
        "/sales/product/location-information",
        { id: productId, locationId, productUrl: url },
        { headers: authHeader() },
      );
      for (const { field } of INFORMATION_PDFS) {
        if (picked[field]) {
          const body = new FormData();
          body.append("file", picked[field]);
          body.append("id", productId);
          body.append("field", field);
          body.append("locationId", locationId);
          await API.patch("/sales/product/information-file", body, {
            headers: { ...authHeader(), "Content-Type": "multipart/form-data" },
          });
        } else if (removing[field] && current[field]) {
          await API.post(
            "/sales/product/information-file/delete",
            { id: productId, field, locationId },
            { headers: authHeader() },
          );
        }
      }
      await loadRows();
      notify("Brand saved", "success");
      closeForm();
    } catch (err) {
      setError(errorText(err));
      await loadRows();
    } finally {
      setSaving(false);
    }
  };

  const removeLocation = async (row) => {
    if (
      !window.confirm(
        `Remove the ${locationName(row.locationId)} override? That brand will use the Default.`,
      )
    )
      return;
    try {
      await API.post(
        "/sales/product/location-information/delete",
        { id: productId, locationId: row.locationId },
        { headers: authHeader() },
      );
      notify("Brand removed", "success");
      await loadRows();
    } catch (err) {
      notify(errorText(err), "error");
    }
  };

  const view = async (key) => {
    try {
      await openStoredPdf(key);
    } catch (err) {
      notify(errorText(err), "error");
    }
  };

  const fileCell = (key) =>
    key ? (
      <button
        type="button"
        className="btn btn-link p-0 text-truncate text-start d-block w-100"
        title={fileNameOf(key)}
        onClick={() => view(key)}
      >
        <i className="mdi mdi-file-pdf-box text-danger"></i> {fileNameOf(key)}
      </button>
    ) : (
      <span className="text-muted">Default</span>
    );

  // Same row as the customer's Tax Exemption Certificate (the approved file
  // UI): the box shows the saved or picked file, else a picker; download and
  // delete sit on the right, grey until there is a file. Delete here only
  // marks the change -- it happens on Save, and Cancel undoes it.
  const fileRow = ({ field, label }) => {
    const saved = removing[field] ? null : current[field];
    const file = picked[field];
    const has = !!(file || saved);
    return (
      <div className="mb-3" key={field}>
        <label htmlFor={`override-${field}`} className="form-label fw-bold mb-1">
          {label}
        </label>
        <div className="d-flex">
          <div className="flex-grow-1" style={{ minWidth: 0 }}>
            {has ? (
              <div
                className="form-control text-truncate"
                title={file ? file.name : fileNameOf(saved)}
              >
                <i className="mdi mdi-file-pdf-box text-danger"></i>{" "}
                {file ? file.name : fileNameOf(saved)}
              </div>
            ) : (
              <input
                id={`override-${field}`}
                type="file"
                accept="application/pdf"
                className="form-control"
                onChange={(e) => {
                  pickFile(field, e.target.files?.[0]);
                  e.target.value = "";
                }}
              />
            )}
          </div>
          <button
            type="button"
            aria-label={`View ${label}`}
            title="View"
            disabled={!saved || !!file || saving}
            onClick={() => view(saved)}
            className={`btn btn-sm btn-${
              saved && !file ? "success" : "secondary"
            } rounded py-0 px-1 ms-1`}
          >
            <i className="mdi mdi-arrow-down-bold fs-3"></i>
          </button>
          <button
            type="button"
            aria-label={`Remove ${label}`}
            title="Remove"
            disabled={!has || saving}
            onClick={() =>
              file
                ? setPicked((prev) => ({ ...prev, [field]: null }))
                : setRemoving((prev) => ({ ...prev, [field]: true }))
            }
            className={`btn btn-${has ? "danger" : "secondary"} rounded ms-1`}
          >
            <i className="mdi mdi-delete"></i>
          </button>
        </div>
      </div>
    );
  };

  // Back in a popup (user, 2026-10-16). It opens on top of the product modal,
  // which is the same white -- the border and heavy shadow keep the two from
  // blending into one sheet.
  const form = (
    <Modal
      show={!!editing}
      onHide={closeForm}
      contentClassName="border border-2 shadow-lg"
      style={{ borderRadius: 15 }}
    >
      <Modal.Body
        className="bg-white px-3 py-3"
        style={{ borderRadius: 15 }}
        role="form"
        aria-label="Brand override"
      >
        {editing && (
          <>
            <h4 className="mb-1">
              {editing.isNew
                ? "Add brand override"
                : `Edit ${locationName(editing.locationId)}`}
            </h4>
            <p className="text-muted small mb-3">
              Anything left empty uses the Default.
            </p>

            {editing.isNew && (
              <div className="mb-3">
                <label htmlFor="override-location" className="form-label fw-bold mb-1">
                  Brand *
                </label>
                <select
                  id="override-location"
                  className="form-select"
                  value={editing.locationId}
                  onChange={(e) =>
                    setEditing((m) => ({ ...m, locationId: e.target.value }))
                  }
                >
                  <option value="">Select brand...</option>
                  {available.map((l) => (
                    <option key={l.id} value={String(l.id)}>
                      {l.locationName}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="mb-3">
              <label htmlFor="override-url" className="form-label fw-bold mb-1">
                Product Url
              </label>
              <input
                id="override-url"
                className="form-control"
                value={url}
                placeholder="Leave empty to use the Default"
                onChange={(e) => setUrl(e.target.value)}
              />
            </div>

            {INFORMATION_PDFS.map(fileRow)}

            {error && (
              <div className="alert alert-danger py-1" role="alert">
                {error}
              </div>
            )}

            <div className="d-flex justify-content-end gap-1">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={closeForm}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={save}
                disabled={saving}
              >
                {saving ? "Saving..." : editing.isNew ? "Add" : "Save"}
              </button>
            </div>
          </>
        )}
      </Modal.Body>
    </Modal>
  );

  return (
    <>
      <div className="col-lg-6">{defaultCard}</div>

      <div className="col-lg-6">
        <div className="card border-0 shadow-sm h-100">
          <div className="card-header bg-secondary bg-opacity-25 border-0 py-1 d-flex align-items-center justify-content-between">
            <h5 className="text-secondary mb-0">
              <i className="mdi mdi-map-marker-multiple-outline mr-1"></i> Brand
              Overrides
            </h5>
            {isUpdateMode && (
              <button
                type="button"
                className="btn btn-sm btn-primary"
                onClick={() => openForm(null)}
                disabled={!available.length}
                title={
                  available.length
                    ? "Add a brand"
                    : "Every brand already has an override"
                }
              >
                <i className="mdi mdi-plus"></i> Add
              </button>
            )}
          </div>
          <div className="card-body p-2">
            {!isUpdateMode ? (
              <div className="text-muted small p-2">
                Save the product first to add brand overrides.
              </div>
            ) : loading ? (
              <div className="text-muted small p-2">Loading…</div>
            ) : !rows.length ? (
              <div className="text-muted small p-2">
                If no brand override is available - every brand will
                use Default.
              </div>
            ) : (
              // Fixed layout: the table always fits the card and long URLs / file
              // names are cut with "…" (full text on hover) -- no sideways scroll.
              <table
                className="table table-sm align-middle mb-0 w-100"
                style={{ tableLayout: "fixed" }}
              >
                  <colgroup>
                    <col style={{ width: "24%" }} />
                    <col style={{ width: "22%" }} />
                    <col style={{ width: "21%" }} />
                    <col style={{ width: "21%" }} />
                    <col style={{ width: "12%" }} />
                  </colgroup>
                  <thead>
                    <tr>
                      <th>Brand</th>
                      <th>URL</th>
                      <th>Images</th>
                      <th>Size Chart</th>
                      <th className="text-end">
                        <span className="visually-hidden">Actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => (
                      <tr
                        key={row.locationId}
                        className={
                          editing?.locationId === row.locationId ? "table-active" : ""
                        }
                      >
                        <td
                          className="fw-bold text-truncate"
                          title={locationName(row.locationId)}
                        >
                          {locationName(row.locationId)}
                        </td>
                        <td className="text-truncate">
                          {row.productUrl ? (
                            <a
                              href={toHref(row.productUrl)}
                              target="_blank"
                              rel="noopener noreferrer"
                              title={row.productUrl}
                            >
                              {row.productUrl}
                            </a>
                          ) : (
                            <span className="text-muted">Default</span>
                          )}
                        </td>
                        <td>{fileCell(row.productImagePdf)}</td>
                        <td>{fileCell(row.productSizeChartPdf)}</td>
                        <td className="text-end text-nowrap">
                          <button
                            type="button"
                            className="btn btn-link p-0 me-2"
                            aria-label={`Edit ${locationName(row.locationId)}`}
                            onClick={() => openForm(row)}
                          >
                            <i className="mdi mdi-pencil"></i>
                          </button>
                          <button
                            type="button"
                            className="btn btn-link p-0 text-danger"
                            aria-label={`Remove ${locationName(row.locationId)}`}
                            onClick={() => removeLocation(row)}
                          >
                            <i className="mdi mdi-delete"></i>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
              </table>
            )}
            <small className="text-muted d-block px-2 mt-2">
              Any field left empty will use the default files or settings.
            </small>
          </div>
        </div>
      </div>

      {form}
    </>
  );
}
